import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Truck, 
  Package, 
  MapPin, 
  Clock, 
  CheckCircle, 
  Shield, 
  ArrowRight,
  ChevronDown,
  HelpCircle,
  Globe,
  Home,
  Building,
  Timer,
  PackageCheck,
  PackageSearch,
  Mail,
  Bell,
  Smartphone,
  CalendarClock,
  BadgeCheck,
  Heart,
  Gift,
  AlertCircle,
  Info,
  Boxes,
  Calculator,
  HandHeart
} from 'lucide-react';

// How shipping works steps
const SHIPPING_STEPS = [
  {
    number: '01',
    title: 'Ordine Confermato',
    description: 'Appena completi il pagamento, l\'artigiano riceve la notifica e inizia a preparare il tuo ordine.',
    icon: CheckCircle,
    color: 'bg-green-500'
  },
  {
    number: '02',
    title: 'Preparazione',
    description: 'L\'artigiano prepara con cura il prodotto, lo imballa con attenzione e genera l\'etichetta di spedizione.',
    icon: Package,
    color: 'bg-blue-500'
  },
  {
    number: '03',
    title: 'Spedizione',
    description: 'Il pacco viene affidato al corriere. Ricevi via email il codice di tracking per seguire la spedizione.',
    icon: Truck,
    color: 'bg-purple-500'
  },
  {
    number: '04',
    title: 'Consegna',
    description: 'Il corriere consegna il pacco all\'indirizzo indicato. Hai 7 giorni per confermare la ricezione.',
    icon: Home,
    color: 'bg-amber-500'
  }
];

// Shipping features
const SHIPPING_FEATURES = [
  {
    icon: PackageSearch,
    title: 'Tracking in Tempo Reale',
    description: 'Ogni spedizione è tracciabile. Ricevi il codice tracking via email e puoi seguire il pacco nella tua dashboard.'
  },
  {
    icon: Bell,
    title: 'Notifiche Automatiche',
    description: 'Ti avvisiamo via email quando l\'ordine viene spedito, quando è in consegna e quando viene consegnato.'
  },
  {
    icon: Shield,
    title: 'Imballaggio Protettivo',
    description: 'Gli artigiani imballano con cura ogni prodotto per garantire che arrivi in perfette condizioni.'
  },
  {
    icon: HandHeart,
    title: 'Spedizione Diretta',
    description: 'Il pacco arriva direttamente dall\'artigiano a casa tua. Nessun passaggio intermedio, massima freschezza.'
  }
];

// Delivery zones
const DELIVERY_ZONES = [
  {
    zone: 'Italia',
    icon: '🇮🇹',
    timing: '2-5 giorni lavorativi',
    description: 'Spedizioni in tutta Italia, isole incluse. Tempi variabili in base alla località e al corriere scelto dall\'artigiano.',
    available: true
  },
  {
    zone: 'Europa',
    icon: '🇪🇺',
    timing: '5-10 giorni lavorativi',
    description: 'Spedizioni in tutti i paesi dell\'Unione Europea. Alcuni artigiani potrebbero non spedire all\'estero.',
    available: true
  },
  {
    zone: 'Mondo',
    icon: '🌍',
    timing: '10-20 giorni lavorativi',
    description: 'Spedizioni internazionali disponibili per alcuni artigiani. Verifica la disponibilità nella scheda prodotto.',
    available: 'limited'
  }
];

// Shipping cost info
const SHIPPING_COSTS_INFO = [
  {
    title: 'Costi Variabili per Artigiano',
    description: 'Ogni artigiano definisce le proprie tariffe di spedizione in base a peso, dimensioni e destinazione. I costi sono sempre visibili prima del checkout.',
    icon: Calculator
  },
  {
    title: 'Spedizione Gratuita',
    description: 'Molti artigiani offrono la spedizione gratuita sopra una certa soglia di spesa. Cerca il badge "Spedizione Gratuita" nelle schede prodotto.',
    icon: Gift
  },
  {
    title: 'Spedizione Combinata',
    description: 'Se acquisti più prodotti dallo stesso artigiano, la spedizione viene combinata. Paghi una sola volta le spese di spedizione.',
    icon: Boxes
  },
  {
    title: 'Ordini Multi-Artigiano',
    description: 'Se acquisti da artigiani diversi, ogni artigiano spedisce separatamente. I costi di spedizione si sommano ma ogni pacco ha il suo tracking.',
    icon: Package
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: 'Quanto costa la spedizione?',
    answer: 'I costi di spedizione variano in base all\'artigiano, al peso/dimensioni del prodotto e alla destinazione. Puoi vedere il costo esatto nel carrello prima di procedere al pagamento. Molti artigiani offrono la spedizione gratuita sopra una certa soglia di spesa.'
  },
  {
    question: 'Quanto tempo ci vuole per ricevere l\'ordine?',
    answer: 'I tempi dipendono da due fattori: il tempo di preparazione dell\'artigiano (indicato nella scheda prodotto) e i tempi del corriere. Per prodotti già pronti, la spedizione avviene in 1-3 giorni. Per prodotti su misura, i tempi sono indicati nella proposta accettata. La consegna in Italia richiede generalmente 2-5 giorni lavorativi.'
  },
  {
    question: 'Come posso tracciare il mio ordine?',
    answer: 'Quando l\'artigiano spedisce il pacco, ricevi una email con il codice di tracking e il link al sito del corriere. Puoi anche seguire lo stato dell\'ordine nella sezione "I miei ordini" della tua dashboard.'
  },
  {
    question: 'Spedite anche all\'estero?',
    answer: 'Dipende dall\'artigiano. Molti spediscono in tutta Europa, alcuni anche nel resto del mondo. Nella scheda prodotto e nel profilo dell\'artigiano puoi verificare le destinazioni disponibili. I costi e i tempi per l\'estero sono generalmente più elevati.'
  },
  {
    question: 'Cosa succede se non sono a casa al momento della consegna?',
    answer: 'Il corriere lascia un avviso e ritenta la consegna il giorno successivo. In alternativa, puoi ritirare il pacco presso il punto di ritiro indicato nell\'avviso. Alcuni corrieri permettono di riprogrammare la consegna online.'
  },
  {
    question: 'Posso far spedire a un indirizzo diverso dal mio?',
    answer: 'Certamente! Durante il checkout puoi indicare qualsiasi indirizzo di spedizione. È perfetto per i regali: puoi far recapitare il pacco direttamente al destinatario.'
  },
  {
    question: 'Il pacco arriverà ben imballato?',
    answer: 'Gli artigiani su Rarifex hanno a cuore i loro prodotti e li imballano con la massima cura. Utilizzano materiali protettivi adeguati al tipo di prodotto. Se hai esigenze particolari (es. confezione regalo), puoi chiederlo direttamente all\'artigiano via messaggio.'
  },
  {
    question: 'Cosa faccio se il pacco arriva danneggiato?',
    answer: 'Se il pacco presenta danni evidenti, fotografa l\'imballaggio e il prodotto prima di accettare la consegna o immediatamente dopo. Contatta l\'artigiano tramite la chat della piattaforma allegando le foto. Non confermare la ricezione finché il problema non è risolto: il pagamento all\'artigiano è trattenuto fino alla tua conferma.'
  },
  {
    question: 'Posso richiedere una data di consegna specifica?',
    answer: 'Puoi indicare preferenze nelle note dell\'ordine, ma non possiamo garantire date specifiche poiché dipende dal corriere. Per regali con date importanti, ti consigliamo di ordinare con anticipo e comunicare l\'urgenza all\'artigiano.'
  },
  {
    question: 'Come funziona se ordino da più artigiani?',
    answer: 'Ogni artigiano spedisce separatamente, quindi riceverai più pacchi (uno per artigiano). Ogni pacco ha il suo tracking e le spese di spedizione sono calcolate separatamente. Se ordini più prodotti dallo stesso artigiano, la spedizione viene combinata.'
  }
];

// Courier partners (examples)
const COURIER_INFO = {
  title: 'Corrieri Utilizzati',
  description: 'Gli artigiani scelgono il corriere più adatto in base al tipo di prodotto e alla destinazione. I più comuni sono:',
  couriers: ['Poste Italiane', 'BRT', 'GLS', 'DHL', 'UPS', 'FedEx', 'SDA']
};

export default function ShippingPage() {
  const [expandedFaq, setExpandedFaq] = useState(null);

  return (
    <>
      <Helmet>
        <title>Spedizioni su Rarifex | Consegna Tracciata | Artigianato Made in Italy</title>
        <meta name="description" content="Scopri come funzionano le spedizioni su Rarifex. Consegna tracciata in tutta Italia ed Europa, imballaggio protettivo, notifiche in tempo reale. Spedizione diretta dall'artigiano a casa tua." />
        <meta name="keywords" content="spedizioni rarifex, consegna artigianato, tracking ordini, spedizione italia, tempi consegna, costi spedizione, pacco tracciato" />
        <link rel="canonical" href="https://www.rarifex.com/spedizioni" />
        <meta property="og:title" content="Spedizioni su Rarifex | Consegna Tracciata" />
        <meta property="og:description" content="Spedizione diretta dall'artigiano a casa tua. Tracking in tempo reale, imballaggio protettivo, notifiche automatiche." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/spedizioni" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-amber-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Truck className="w-4 h-4" />
                Spedizioni Tracciate
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Dall'artigiano{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-500">
                  a casa tua
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed max-w-3xl mx-auto">
                Ogni prodotto viene spedito direttamente dall'artigiano che lo ha creato. 
                <strong className="text-slate-800"> Tracking in tempo reale, imballaggio curato, consegna sicura.</strong>
              </p>

              {/* Key features */}
              <div className="flex flex-wrap justify-center gap-6 md:gap-10 mt-10">
                <div className="flex items-center gap-2 text-slate-700">
                  <PackageSearch className="w-5 h-5 text-amber-500" />
                  <span className="text-sm font-medium">Tracking Incluso</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <Bell className="w-5 h-5 text-amber-500" />
                  <span className="text-sm font-medium">Notifiche Email</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <Shield className="w-5 h-5 text-amber-500" />
                  <span className="text-sm font-medium">Imballaggio Protettivo</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Come funziona la spedizione
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Un processo semplice e trasparente, dal pagamento alla consegna.
                </p>
              </div>

              <div className="grid md:grid-cols-4 gap-6">
                {SHIPPING_STEPS.map((step, index) => (
                  <div key={index} className="relative">
                    {index < SHIPPING_STEPS.length - 1 && (
                      <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-slate-200 -translate-x-1/2 z-0" />
                    )}
                    <Card className="p-6 bg-white border-slate-200 hover:shadow-lg transition-all relative z-10 h-full">
                      <div className={`w-12 h-12 ${step.color} rounded-xl flex items-center justify-center mb-4`}>
                        <step.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-xs font-bold text-slate-400 mb-2">{step.number}</div>
                      <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
                      <p className="text-sm text-slate-600">{step.description}</p>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SHIPPING FEATURES */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Spedizioni curate nei dettagli
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Ogni artigiano ha a cuore che il suo prodotto arrivi perfetto.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {SHIPPING_FEATURES.map((feature, index) => (
                  <Card key={index} className="p-6 bg-white border-slate-200 hover:border-amber-300 hover:shadow-lg transition-all">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-amber-600" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-sm text-slate-600">{feature.description}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* DELIVERY ZONES */}
        <section className="py-16 md:py-24 bg-slate-900">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Dove spediamo
                </h2>
                <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                  I nostri artigiani spediscono in tutta Italia e, in molti casi, anche all'estero.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {DELIVERY_ZONES.map((zone, index) => (
                  <div 
                    key={index}
                    className={`rounded-2xl p-6 border ${
                      zone.available === true 
                        ? 'bg-slate-800/50 border-slate-700/50' 
                        : 'bg-slate-800/30 border-slate-700/30'
                    }`}
                  >
                    <div className="text-4xl mb-4">{zone.icon}</div>
                    <h3 className="text-xl font-bold text-white mb-2">{zone.zone}</h3>
                    <div className="flex items-center gap-2 text-amber-400 mb-3">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">{zone.timing}</span>
                    </div>
                    <p className="text-sm text-slate-400">{zone.description}</p>
                    {zone.available === 'limited' && (
                      <div className="mt-3 inline-flex items-center gap-1 text-xs text-slate-500">
                        <Info className="w-3 h-3" />
                        Disponibilità limitata
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SHIPPING COSTS */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Calculator className="w-4 h-4" />
                  Costi Trasparenti
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Come funzionano i costi di spedizione
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Ogni artigiano definisce le proprie tariffe. I costi sono sempre visibili prima del pagamento.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {SHIPPING_COSTS_INFO.map((info, index) => (
                  <Card key={index} className="p-6 bg-white border-slate-200">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-2">{info.title}</h3>
                        <p className="text-sm text-slate-600">{info.description}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Free shipping tip */}
              <div className="mt-10 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 md:p-8 border border-green-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Gift className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-2">Cerca la spedizione gratuita!</h3>
                    <p className="text-slate-600">
                      Molti artigiani offrono la spedizione gratuita sopra una certa soglia di spesa. 
                      Nelle schede prodotto e nei profili artigiano trovi sempre indicate le condizioni 
                      per la spedizione gratuita. A volte basta aggiungere un piccolo accessorio per raggiungerla!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TRACKING INFO */}
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <PackageSearch className="w-4 h-4" />
                    Tracking
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                    Segui il tuo pacco in tempo reale
                  </h2>
                  <p className="text-lg text-slate-600 mb-6">
                    Ogni spedizione è tracciata. Ricevi il codice tracking via email appena l'artigiano 
                    spedisce il pacco, e puoi seguire ogni passaggio fino alla consegna.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">Email di conferma spedizione</h4>
                        <p className="text-sm text-slate-600">Con codice tracking e link diretto al corriere</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Smartphone className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">Dashboard personale</h4>
                        <p className="text-sm text-slate-600">Stato aggiornato di tutti i tuoi ordini in un'unica pagina</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Bell className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">Notifiche automatiche</h4>
                        <p className="text-sm text-slate-600">Ti avvisiamo quando il pacco è in consegna</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Visual representation */}
                <div className="bg-white rounded-2xl shadow-xl p-6 border border-slate-200">
                  <div className="text-sm font-medium text-slate-500 mb-4">Stato ordine #RF-2024-1234</div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">Ordine confermato</div>
                        <div className="text-xs text-slate-500">12 Gen, 10:30</div>
                      </div>
                    </div>
                    
                    <div className="ml-4 border-l-2 border-green-300 h-6"></div>
                    
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">In preparazione</div>
                        <div className="text-xs text-slate-500">12 Gen, 14:00</div>
                      </div>
                    </div>
                    
                    <div className="ml-4 border-l-2 border-green-300 h-6"></div>
                    
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">Spedito</div>
                        <div className="text-xs text-slate-500">13 Gen, 09:15 • Tracking: BRT1234567890</div>
                      </div>
                    </div>
                    
                    <div className="ml-4 border-l-2 border-amber-300 h-6"></div>
                    
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center animate-pulse">
                        <Truck className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-amber-600">In transito</div>
                        <div className="text-xs text-slate-500">Hub di Milano - In consegna oggi</div>
                      </div>
                    </div>
                    
                    <div className="ml-4 border-l-2 border-slate-200 border-dashed h-6"></div>
                    
                    <div className="flex items-center gap-3 opacity-50">
                      <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                        <Home className="w-5 h-5 text-slate-400" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-slate-500">Consegnato</div>
                        <div className="text-xs text-slate-400">In attesa</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* COURIERS */}
        <section className="py-12 md:py-16 bg-white border-y border-slate-100">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-xl font-bold text-slate-900 mb-3">{COURIER_INFO.title}</h3>
              <p className="text-slate-600 mb-6">{COURIER_INFO.description}</p>
              <div className="flex flex-wrap justify-center gap-4">
                {COURIER_INFO.couriers.map((courier, index) => (
                  <span 
                    key={index}
                    className="px-4 py-2 bg-slate-100 rounded-full text-sm font-medium text-slate-700"
                  >
                    {courier}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* IMPORTANT NOTICE */}
        <section className="py-12 md:py-16 bg-amber-50 border-b border-amber-100">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-8 h-8 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    Conferma sempre la ricezione
                  </h3>
                  <p className="text-slate-700 mb-4">
                    Quando ricevi il pacco, accedi alla tua dashboard e conferma la ricezione entro 7 giorni. 
                    Solo dopo la tua conferma il pagamento verrà rilasciato all'artigiano.
                  </p>
                  <p className="text-slate-600 text-sm">
                    Se non confermi entro 7 giorni e non apri contestazioni, il sistema considera l'ordine 
                    completato e rilascia automaticamente il pagamento.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <HelpCircle className="w-4 h-4" />
                  Domande Frequenti
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Hai domande sulle spedizioni?
                </h2>
              </div>

              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="font-medium text-slate-900 pr-4">{faq.question}</span>
                      <ChevronDown 
                        className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                          expandedFaq === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {expandedFaq === index && (
                      <div className="px-6 pb-4">
                        <p className="text-slate-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-amber-500 to-orange-500">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Truck className="w-12 h-12 text-white/80 mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Pronto per ricevere il tuo pezzo unico?
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Esplora le creazioni artigianali Made in Italy e scopri la magia 
                di ricevere un prodotto fatto apposta per te.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/products">
                  <Button size="lg" className="bg-white text-amber-600 hover:bg-slate-100">
                    <Package className="w-5 h-5 mr-2" />
                    Esplora i Prodotti
                  </Button>
                </Link>
                <Link to="/come-acquistare">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    <ArrowRight className="w-5 h-5 mr-2" />
                    Come Acquistare
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
